import React, { forwardRef } from "react";
import logo from "../assets/logo.png";
import { getVehicleInfo } from "./billingVehicle";

const Receipt = forwardRef(({ sale, cartItems }, ref) => {
  // Helper for currency
  const formatLKR = (amount) => {
    return new Intl.NumberFormat("en-LK", {
      style: "currency",
      currency: "LKR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // 1. Determine items source
  const rawItems = sale ? sale.items : cartItems || [];

  // 2. Process items to calculate finals
  let grandTotal = 0;
  let totalSavings = 0;

  const items = rawItems.map((item) => {
    const originalPrice = parseFloat(item.unit_price || item.sell_price || 0);
    const qty = parseInt(item.quantity || 1);

    let discountAmount = 0;

    // CHECK 1: Backend History (Stored as 'discount' cash value)
    if (item.discount !== undefined && item.discount !== null) {
      discountAmount = parseFloat(item.discount);
    }
    // CHECK 2: Live Cart (Stored as 'discountPercent')
    else if (item.discountPercent !== undefined) {
      discountAmount = originalPrice * (parseFloat(item.discountPercent) / 100);
    }

    const finalPrice = originalPrice - discountAmount;
    const lineTotal = finalPrice * qty;

    grandTotal += lineTotal;
    totalSavings += discountAmount * qty;

    return {
      name: item.part_name || item.name || item.description || "Item",
      part_number: item.part_number,
      isLabor: item.item_type === "LABOR",
      qty,
      originalPrice,
      finalPrice,
      discountAmount,
      lineTotal,
    };
  });

  // Parts first, then the repairs done — same grouping as the A4 invoice, so
  // the two documents describe a sale the same way.
  const partItems = items.filter((i) => !i.isLabor);
  const laborItems = items.filter((i) => i.isLabor);

  const renderRows = (list, offset) =>
    list.map((item, index) => (
      <tr key={`${offset}-${index}`}>
        <td className="py-2 pr-1 align-top">
          <div className="font-bold">{item.name}</div>
          {item.part_number && (
            <div className="text-[9px] text-gray-500">{item.part_number}</div>
          )}
        </td>
        <td className="py-2 text-center align-top">{item.qty}</td>
        <td className="py-2 text-right align-top">
          {item.discountAmount > 0 ? (
            <div className="flex flex-col items-end leading-none space-y-0.5">
              <span className="line-through text-[9px] text-gray-400">
                {item.originalPrice.toFixed(2)}
              </span>
              <span className="font-bold">{item.finalPrice.toFixed(2)}</span>
            </div>
          ) : (
            <span>{item.originalPrice.toFixed(2)}</span>
          )}
        </td>
        <td className="py-2 text-right font-bold align-top">
          {item.lineTotal.toFixed(2)}
        </td>
      </tr>
    ));

  // Meta Info
  const date = sale
    ? new Date(sale.created_at).toLocaleString()
    : new Date().toLocaleString();
  const invoiceId = sale ? sale.id.substring(0, 8).toUpperCase() : "NEW";
  const customer = sale ? sale.customer_name : "Walk-in";
  // Shared with the A4 invoice so the two documents can't disagree.
  const vehicleInfo = getVehicleInfo(sale);

  return (
    <div
      ref={ref}
      className="p-4 bg-white text-black font-mono text-xs leading-tight max-w-[80mm] mx-auto min-h-screen"
      style={{ width: "100%" }}
    >
      {/* HEADER */}
      <div className="flex flex-col items-center text-center mb-4">
        {/* Width-only sizing (never a fixed square): html2canvas ignores
            object-fit when rasterizing the receipt for WhatsApp/download, so a
            box that doesn't match the logo's ~2.4:1 aspect ratio comes out
            stretched even though the browser letterboxes it correctly. */}
        <img
          src={logo}
          alt="Logo"
          className="w-28 h-auto mb-2 grayscale"
        />
        <h1 className="text-xl font-bold uppercase tracking-wider">
          NSS Auto Spares
        </h1>
        <div className="mt-2 flex flex-col items-center space-y-1 text-[10px]">
          <span>No. 272 Thudella, Ja-ela</span>
          <span>+94 71 618 8187</span>
        </div>
      </div>

      <div className="border-b-2 border-black border-dashed my-2"></div>

      {/* META */}
      <div className="flex flex-col space-y-1 mb-2">
        <div className="flex justify-between">
          <span>Date:</span>
          <span>{date}</span>
        </div>
        <div className="flex justify-between">
          <span>Invoice:</span>
          <span className="font-bold">{invoiceId}</span>
        </div>
        <div className="flex justify-between">
          <span>Customer:</span>
          <span className="text-right">{customer}</span>
        </div>
        {vehicleInfo.plate && (
          <div className="flex justify-between">
            <span>Vehicle:</span>
            <span className="text-right font-bold">
              {vehicleInfo.plate}
              {vehicleInfo.makeModel && (
                <span className="block font-normal">{vehicleInfo.makeModel}</span>
              )}
            </span>
          </div>
        )}
        {vehicleInfo.mileageLabel && (
          <div className="flex justify-between">
            <span>Mileage:</span>
            <span className="text-right">
              {vehicleInfo.mileageLabel}
              {/* The reading often predates this job — say when it was taken. */}
              {vehicleInfo.mileageDate && (
                <span className="block text-[10px]">rec. {vehicleInfo.mileageDate}</span>
              )}
            </span>
          </div>
        )}
      </div>

      <div className="border-b-2 border-black border-dashed mb-2"></div>

      {/* TABLE */}
      <table className="w-full text-left mb-2">
        <thead>
          <tr className="text-[10px] uppercase border-b border-black border-dotted">
            <th className="pb-1 w-[40%]">Item</th>
            <th className="pb-1 text-center w-[15%]">Qty</th>
            <th className="pb-1 text-right w-[25%]">Price</th>
            <th className="pb-1 text-right w-[20%]">Total</th>
          </tr>
        </thead>
        <tbody className="text-[11px]">
          {renderRows(partItems, 0)}
          {renderRows(laborItems, partItems.length)}
        </tbody>
      </table>

      <div className="border-b-2 border-black border-dashed mb-2"></div>

      {/* TOTALS */}
      <div className="flex flex-col space-y-1 text-right mb-4">
        {totalSavings > 0 && (
          <div className="flex justify-between text-sm text-gray-500">
            <span>Subtotal:</span>
            <span>{formatLKR(grandTotal + totalSavings)}</span>
          </div>
        )}
        {totalSavings > 0 && (
          <div className="flex justify-between text-sm font-bold">
            <span>Discount:</span>
            <span>- {formatLKR(totalSavings)}</span>
          </div>
        )}
        <div className="flex justify-between text-xl font-bold border-t border-black border-dotted pt-2 mt-1">
          <span>TOTAL:</span>
          <span>{formatLKR(grandTotal)}</span>
        </div>
        {sale && sale.payment_status === "PARTIAL" && (
          <>
            <div className="flex justify-between text-sm font-bold">
              <span>Paid Now:</span>
              <span>{formatLKR(sale.amount_paid)}</span>
            </div>
            <div className="flex justify-between text-sm font-bold">
              <span>Balance Due:</span>
              <span>{formatLKR(parseFloat(sale.total_amount) - parseFloat(sale.amount_paid || 0))}</span>
            </div>
          </>
        )}
        {sale && sale.payment_status === "CREDIT" && (
          <div className="flex justify-between text-sm font-bold">
            <span>Payment:</span>
            <span>CREDIT (Pay Later)</span>
          </div>
        )}
        {totalSavings > 0 && (
          <div className="text-center text-[10px] font-bold border border-black rounded p-1 mt-3">
            *** YOU SAVED {formatLKR(totalSavings)} ***
          </div>
        )}
      </div>

      <div className="text-center space-y-2 border-t border-black border-dotted pt-4">
        <p className="font-bold uppercase">Important Notice</p>
        <p className="text-[10px] leading-tight text-justify">
          Please retain receipt for warranty. No refunds on Electrical Parts.
        </p>
        <p className="font-bold text-sm mt-2">Thank You!</p>
      </div>
    </div>
  );
});

export default Receipt;
